import { setGetAuthTokenModule, setRefreshTokensModule } from '@quickmd-llc/frontend-utils'
import { store } from 'app/store'
import { postAuthRefreshtoken } from './generated/auth/auth'
import { LOGOUT, REFRESH_TOKEN_SUCCESS } from 'store/auth/AuthConstants'
import { describeRefreshError, isFatalRefreshError, isUnifiedRefreshEnabled, refreshSession } from './refreshSession'

const getAuthToken = () => store.getState().auth.accessToken

/**
 * Refresh hook handed to the frontend-utils Orval fetcher.
 *
 * When `unified_token_refresh` is ON, delegates to the shared single-flight
 * `refreshSession()`, which is essential here: the fetcher has no de-duplication of
 * its own, so an expiry that 401s N mounted queries would otherwise fire N concurrent
 * refreshes — and the losers of that race get a 401 back for presenting an
 * already-rotated token.
 *
 * The legacy path below (flag OFF) keeps its original transport — `postAuthRefreshtoken`
 * through the Orval client — but no longer logs out on every thrown value. It used to
 * `catch (e) → LOGOUT` unconditionally, and the fetcher throws `buildAppError(...)` for
 * EVERY non-ok response, so a 429, a 500, or a 401 caused by a sibling refresh all ended
 * the provider's session: 5,710 `orval-refresh-failed` Sentry events. Since the flag is
 * OFF in production, that catch-all *is* the outage. It now defers to
 * `isFatalRefreshError`, so only a genuine auth rejection ends the session and both flag
 * states are safe to run.
 *
 * (`buildAppError` returns a plain object rather than an Error, so `e instanceof Error`
 * is false and the Sentry context recorded a useless "[object Object]" — the message is
 * now extracted from the error's own `message` field instead.)
 */
/**
 * True while the legacy refresh below is awaiting its own HTTP call.
 *
 * The legacy path deliberately keeps its original transport — `postAuthRefreshtoken`,
 * which routes through the SAME frontend-utils fetcher that calls this function on a
 * 401 (`api/generated/auth/auth.ts:337`). So if the refresh endpoint ever answers 401,
 * the fetcher handling the refresh request calls us again, we start another
 * `postAuthRefreshtoken`, and each nested level opens a fresh fetcher at `attempt = 0`:
 * unbounded async recursion, not a bounded retry.
 *
 * Returning `false` breaks the cycle at the first nesting level. The fetcher's
 * `if (refreshed) continue` fails, it falls to `throw await buildAppError(response, …)`,
 * and that 401 propagates into our own `catch` below — where `isFatalRefreshError`
 * already classifies 401 as fatal and ends the session once, with a reason tag.
 *
 * NOT the shared in-flight promise: returning it would deadlock, since the re-entrant
 * caller is the very request that promise is waiting on.
 *
 * Concurrent siblings (a burst of queries 401ing at once) also get `false` and fail one
 * request each, instead of stampeding the endpoint with N refreshes over a rotating
 * token — the losers of that race were being handed a 401 for presenting an
 * already-rotated token. This is the crude single-flight the flag-OFF path never had;
 * `refreshSession` does it properly.
 *
 * Those losers therefore depend on the caller retrying: `api/components/queryClient`'s retry
 * predicate replays a 401/403 once while the session is still live, and `makeRequest`
 * re-reads the token via `getAuthTokenModule()` on every attempt.
 *
 * That replay carries the winner's rotated token only if the winner has ALREADY dispatched
 * `REFRESH_TOKEN_SUCCESS` when the retry fires — there is no coupling between the two, just
 * a timing gap the retry usually wins. React Query's default `retryDelay` (exponential,
 * ~1s for the first retry) is deliberately left un-overridden below, which is what buys the
 * refresh POST its head start. A refresh slower than that delay loses the race, and the
 * loser burns its single retry on the pre-rotation token. This is a real if narrow hole,
 * and it is on the DEFAULT path (`unified_token_refresh` is a kill-switch that defaults
 * off) — treat it as the known limit of the crude single-flight, not as a guarantee.
 *
 * Do NOT "fix" it by returning a shared in-flight promise to these losers. It deadlocks:
 * `postAuthRefreshtoken` routes through the same fetcher, `isUnauthorized` matches 401 AND
 * 403, so a refresh endpoint that answers either re-enters this function; awaiting a promise
 * gated on the very request the caller is processing wedges the API layer permanently for
 * the life of the tab. The fetcher passes no request context (`refreshTokensModule()` takes
 * no arguments), so a sibling and the re-entrant refresh are indistinguishable here. A real
 * queue requires either a discriminator from frontend-utils or a non-fetcher transport for
 * the refresh — and the latter would collapse the kill-switch into `refreshSession`, leaving
 * nothing to fall back TO. `refreshSession` already solves this properly; the fix is to turn
 * the flag on, not to rebuild it here.
 *
 * Likewise do not narrow that predicate back to "never retry auth errors" — a refresh race
 * would then silently strand a fan-out of queries with no data and no error.
 */
let legacyRefreshInFlight = false

const refreshTokens = async () => {
  // Unified single-flight refresh: shared with restclient, retries transient
  // failures, and logs out only on a genuine auth rejection.
  if (isUnifiedRefreshEnabled()) {
    return (await refreshSession()) === 'refreshed'
  }
  if (legacyRefreshInFlight) {
    return false
  }
  const authToken = getAuthToken()
  // No token means there is no session to renew — not a session the backend rejected.
  // Without this guard a 401 on a logged-out page posts `{"AccessToken":""}`, the endpoint
  // answers 400 TokenNotValid, `isFatalRefreshError` reads that as a genuine rejection and
  // dispatches LOGOUT — on a tab that was never signed in. `logoutTeardownMiddleware` then
  // runs the non-navigating teardown, which pauses the persistor for the rest of the tab's
  // life, so the login the provider performs next is never written to storage and the next
  // reload signs them out again. `refreshSession` already guards this (its `no-session`
  // outcome) and `restclient` guards it via `JWTRefreshObject()`; this path did not.
  if (!authToken) {
    return false
  }
  try {
    legacyRefreshInFlight = true
    const response = await postAuthRefreshtoken({ AccessToken: authToken })
    if (response.AccessToken) {
      store.dispatch({ type: REFRESH_TOKEN_SUCCESS, payload: { AccessToken: response.AccessToken } })
      return true
    }
    return false
  } catch (e) {
    // Only a genuine auth rejection ends the session. A 429/5xx/network blip leaves it
    // intact — the caller just sees the request fail, and the next 401 refreshes again.
    if (isFatalRefreshError(e)) {
      store.dispatch({
        type: LOGOUT,
        reason: 'orval-refresh-failed',
        logoutContext: { message: describeRefreshError(e) },
      })
    }
    return false
  } finally {
    legacyRefreshInFlight = false
  }
}

export function initOrvalTokenModules() {
  setGetAuthTokenModule(getAuthToken)
  setRefreshTokensModule(refreshTokens)
}
